// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck: File is not being used
import { useQuasar } from 'quasar'
import Talk from 'talkjs'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useConfig } from '@/composables/useConfig'
import axios from '@/plugins/axios'
import enums from '@/plugins/enums'
import utils from '@/plugins/utils'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from './query'

const useTalkjs = ({ curUser, curRequest }) => {
  const { t } = useI18n()
  const config = useConfig()
  const router = useRouter()
  const $q = useQuasar()

  const ORGANIZATIONS = {
    argenta: 'Argenta',
    beobank: 'Beobank',
    credimo: 'Credimo',
    ing: 'ING',
    keytrade: 'Keytrade',
    keytradebank: 'Keytrade',
    medirect: 'MeDirect',
    recordcredits: 'RecordCredits',
    rockestate: 'Rock.estate',
  }

  const SUPPORT_USER = {
    id: `${config.ENVIRONMENT_TYPE}:rockestate_support`,
    name: 'Rockestate Support',
    email: 'support@rock.estate',
    photoUrl: 'https://drs4irmpihjae.cloudfront.net/account-group.svg',
    role: 'support',
  }

  const TAGS = [
    'valuation_challenged',
    'property_sent_onsite',
    'valuation_not_performed_after_first_submission',
    'sla_not_followed',
    'issue_with_appointment',
    'assistance_to_submit',
    'urgent_request',
    'technical_support',
    'other_support',
    'valid_complaint',
  ]

  const ARCHIVED_TAG = 'archived'
  const SUPPORT_INVOLVED_TAG = 'is_support_involved'

  const EXISTS_FILTER = 'exists'
  const NOT_EXISTS_FILTER = '!exists'

  const INBOX_ID = 'talkjs-container'
  const SUPPORT_INBOX_ID = 'talkjs-support-container'
  const CHATBOX_ID = 'talkjs-chatbox-container'
  const TAG_PREFIX = 'tag:'

  if (!curUser) {
    console.error('Talk.js requires curUser to be defined.')
    return
  }

  if (!curRequest) {
    console.error('Talk.js requires curRequest to be defined.')
    return
  }

  const previousRouteName = ref()
  const language = ref()
  const supportSession = ref(null)
  const session = ref(null)
  const popup = ref(null)
  const inbox = ref(null)
  const participantsSubscription = ref(null)
  const showGeneralInbox = ref(false)

  const conversation = ref(null)
  const participants = ref(null)

  const totalUnreads = ref(0)
  const showTags = ref(false)
  const showAddUser = ref(false)
  const showRemoveUser = ref(false)
  const filters = ref({ custom: { archived: NOT_EXISTS_FILTER } })
  const tags = ref([])

  const enabled = computed(() => config.TALKJS_APP_ID && config.TALKJS_APP_ID !== '')

  watch(
    () => curUser.authenticated,
    (newVal) => {
      if (!enabled.value || session.value) {
        return
      }

      if (!newVal) {
        destroyTalkPopup()
        destroyInbox()
        return
      }

      startSession()
    }
  )

  watch(
    [
      /**
       * do not use curRequest.requestRef because that reads from the url instead
       * of the api response and that might mean we haven't loaded the request yet
       */
      () => curRequest.data?.valuation_request_ref,
      () => router.currentRoute.value.name,
      () => curUser.data.preferred_lang,
    ],
    ([_requestRef, routeName, preferredLang]) => {
      const _previousRoute = previousRouteName.value
      previousRouteName.value = routeName
      if (!enabled.value) {
        return
      }

      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      if (urlParams.get('static')) {
        console.debug('Skipping talkjs popup for report')
        destroyTalkPopup()
        return
      }

      switch (preferredLang) {
        case 'fr-BE':
          language.value = 'fr-FR'
          break
        case 'nl-BE':
          language.value = 'nl-NL'
          break
        case 'de-BE':
          language.value = 'de-DE'
          break
        default:
          language.value = 'en-EN'
      }

      /**
       * If the user switches languages while in the inbox, we need to recreate the
       * inbox to ensure the new language is applied.
       * We can't always call createInbox() everytime to avoid unnecessary
       * 'WebSocket is closed before the connection is established' warnings
       */
      if (routeName === 'inbox' && _previousRoute === 'inbox') {
        createInbox()
      }

      /* remove listeners and components */
      if (routeName !== 'inbox' && _previousRoute === 'inbox') {
        destroyInbox()
        return
      }

      /* popup should not be shown in the inbox */
      if (routeName === 'inbox') {
        destroyTalkPopup()

        return
      }

      if (session.value?.currentUser) {
        createTalkPopup()
      }
    }
  )

  watch(
    () => showGeneralInbox.value,
    async () => {
      await createInbox()
    }
  )

  function getCurrentSession() {
    return showGeneralInbox.value ? supportSession.value : session.value
  }

  function startSession() {
    return Talk.ready.then(() => {
      session.value = new Talk.Session({
        appId: config.TALKJS_APP_ID,
        userId: `${config.ENVIRONMENT_TYPE}:${curUser.userId}`,
        token: curUser.data.user_talkjs_token,
      })

      const talkjsUser = {
        name: curUser.userLabel,
        email: curUser.data.email,
        role: curUser.is.support ? 'support' : 'default',
        custom: { association: curUser.data.association },
      }

      session.value.currentUser.createIfNotExists(talkjsUser)
      session.value.currentUser.set(talkjsUser)

      session.value.unreads.onChange(function (unreadConversations) {
        totalUnreads.value = unreadConversations[0]?.unreadMessageCount || 0
      })
    })
  }

  function createInbox() {
    return new Promise((resolve) => {
      destroyInbox()
      resolve(showGeneralInbox.value ? createTalkSupportInbox() : createTalkInbox())
    })
  }

  function destroyTalkPopup() {
    updateConversation()

    if (popup.value?.destroy) {
      popup.value.destroy()
      popup.value = null
    }
  }

  function destroyInbox() {
    updateConversation()

    if (inbox.value?.destroy) {
      inbox.value.destroy()
      inbox.value = null
    }
  }

  function createTalkPopup(showPopup = false) {
    destroyTalkPopup()
    const id = curRequest.data?.valuation_request_ref

    if (!session.value) {
      console.error('Talk session not started. Call startSession() first.')
      return
    }

    /**
     * do not use curRequest.requestRef because that reads from the url instead
     * of the api response and that might mean we haven't loaded the request yet
     */
    if (curUser.is.support && !id) {
      return
    }

    const conversationId = id
      ? `${config.ENVIRONMENT_TYPE}:request:${curRequest.data?.customer}:${id}`
      : `${config.ENVIRONMENT_TYPE}:user:${curUser.userId}`

    return Talk.ready.then(() => {
      session.value.currentUser.set({ locale: language.value })
      const other = session.value.user(SUPPORT_USER.id)
      other.createIfNotExists(SUPPORT_USER)

      const supportPath = router.resolve({ name: 'knowledge-base' })
      const supportUrl = new URL(supportPath.href, window.location.origin)
      const conversation = session.value.conversation(conversationId)
      conversation.createIfNotExists({
        subject: id
          ? `${t('monolith.inbox.support_request')}${t('punctuation.:')} ${id}`
          : t('monolith.inbox.general_inquiry'),
        welcomeMessages: [
          t('monolith.inbox.welcome_1'),
          t('monolith.inbox.welcome_2', {
            url: `<${supportUrl}|help center>`,
            interpolation: { escapeValue: false },
          }),
        ],
      })
      conversation.participant(other).createIfNotExists()

      popup.value = session.value.createPopup({
        theme: {
          custom: {
            disableActions: true,
            disableLink: true,
            translations: _getTranslations(),
          },
        },
      })
      popup.value.select(conversation)
      popup.value.mount({ show: showPopup })
    })
  }

  function createTalkInbox() {
    return Talk.ready.then(() => {
      if (!session.value) {
        return
      }

      inbox.value = session.value.createInbox({
        theme: {
          custom: {
            disableActions: !curUser.is.support,
            links: _getThemeLinks(),
            translations: _getTranslations(),
          },
        },
      })

      mountInboxInstance(INBOX_ID, session.value)
    })
  }

  function openPopup() {
    if (popup.value) popup.value.show()
    else createTalkPopup(true)
  }

  function createTalkSupportInbox() {
    if (!curUser.data.rockestate_talkjs_token) {
      return
    }

    return Talk.ready.then(() => {
      if (!supportSession.value) {
        supportSession.value = new Talk.Session({
          appId: config.TALKJS_APP_ID,
          userId: SUPPORT_USER.id,
          token: curUser.data.rockestate_talkjs_token,
        })
      }
      supportSession.value.currentUser.createIfNotExists(SUPPORT_USER)
      inbox.value = supportSession.value.createInbox({
        messageField: {
          visible: false,
        },
        theme: {
          custom: {
            links: _getThemeLinks(),
            translations: _getTranslations(),
          },
        },
      })

      mountInboxInstance(SUPPORT_INBOX_ID, supportSession.value)
    })
  }

  async function mountInboxInstance(elementId, sessionInstance) {
    sessionInstance.currentUser.set({ locale: language.value })

    inbox.value.mount(document.getElementById(elementId))
    setFilters(showGeneralInbox.value ? [] : ['is_support_involved'])

    inbox.value.onConversationSelected((event) => {
      updateConversation(event.conversation?.id)
    })

    inbox.value.onLeaveConversation((event) => {
      // prevent SUPPORT_USER from leaving
      if (showGeneralInbox.value) {
        $q.notify({
          message: 'Leaving a conversation is not allowed in the General Inbox.',
          type: enums.feedbackType.NEGATIVE,
          timeout: 0,
        })
        event.preventDefault()
      }
    })

    inbox.value.onCustomConversationAction('archive', async () => {
      const snapshot = await conversation.value.get()
      const archived = snapshot.custom?.archived === 'true' ? null : 'true'
      conversation.value.set({ custom: { archived } })
    })

    inbox.value.onCustomConversationAction('tag', async () => {
      const snapshot = await conversation.value.get()
      const _tags = []

      for (const [key, value] of Object.entries(snapshot.custom)) {
        if (key.startsWith(TAG_PREFIX) && value === 'true') _tags.push(key.replace(TAG_PREFIX, ''))
      }

      tags.value = _tags
      showTags.value = true
    })

    inbox.value.onCustomConversationAction('addUser', async () => {
      showAddUser.value = true
    })

    inbox.value.onCustomConversationAction('removeUser', async () => {
      showRemoveUser.value = true
    })
  }

  function selectConversationOverview(conversationId) {
    const conversation = supportSession.value.conversation(conversationId)
    const chatbox = supportSession.value.createChatbox({
      theme: {
        custom: {
          disableActions: true,
          links: _getThemeLinks(),
          translations: _getTranslations(),
        },
      },
      messageField: {
        visible: false,
      },
    })
    chatbox.select(conversation)
    chatbox.mount(document.getElementById(CHATBOX_ID))
  }

  async function setFilters(newFilters = []) {
    if (!inbox.value) {
      return
    }

    const _filters = {
      custom: {
        [ARCHIVED_TAG]: NOT_EXISTS_FILTER,
        [SUPPORT_INVOLVED_TAG]: NOT_EXISTS_FILTER,
      },
    }

    if (newFilters.find((item) => item === ARCHIVED_TAG)) {
      _filters.custom[ARCHIVED_TAG] = EXISTS_FILTER
    }
    if (newFilters.find((item) => item === SUPPORT_INVOLVED_TAG)) {
      _filters.custom[SUPPORT_INVOLVED_TAG] = EXISTS_FILTER
    }

    newFilters.forEach((item) => {
      if (item !== ARCHIVED_TAG && item !== SUPPORT_INVOLVED_TAG) {
        _filters.custom[`${TAG_PREFIX}${item}`] = EXISTS_FILTER
      }
    })

    filters.value = _filters

    await inbox.value.setFeedFilter(_filters)
    await inbox.value.select(undefined)
  }

  async function updateConversation(id) {
    if (!id) {
      conversation.value = null
      participants.value = []

      return
    }

    conversation.value = await getCurrentSession().conversation(id)
    subscribeParticipants()
  }

  function updateTags(value) {
    tags.value = value
  }

  function persistTags() {
    if (!conversation.value) {
      return
    }

    const custom = {}
    TAGS.forEach((tag) => {
      custom[`${TAG_PREFIX}${tag}`] = tags.value.includes(tag) ? 'true' : null
    })

    conversation.value.set({ custom })
  }

  async function addUserToConversation(user) {
    if (!conversation.value) {
      return
    }

    const userId = `${config.ENVIRONMENT_TYPE}:${user.id}`
    const userRef = getCurrentSession().user(userId)
    const talkjsUser = {
      name: user.displayName,
      email: user.email,
      role: utils.containsRole(user.roles, 'dispatcher') ? 'support' : 'default',
      custom: { association: user.association },
    }

    userRef.createIfNotExists(talkjsUser)
    userRef.set(talkjsUser)
    conversation.value.participant(userId).createIfNotExists()
  }

  async function removeUserFromConversation(userId) {
    if (!conversation.value) {
      return
    }
    conversation.value.participant(userId)?.delete()
  }

  function getConversations(userId) {
    const { url } = queryKeyToUrl([
      ReApi.AUTH,
      ReApiVersion.CURRENT,
      ReEndpoint.USER,
      userId,
      'support',
      'conversation',
    ])

    return axios.get(url).then((response) => response.data.filter((item) => item.last_message))
  }

  async function subscribeParticipants() {
    if (participantsSubscription.value) {
      participantsSubscription.value.unsubscribe()
      participantsSubscription.value = null
      participants.value = null
    }

    if (!conversation.value) {
      return
    }

    participantsSubscription.value = conversation.value.subscribeParticipants((snapshot) => {
      participants.value = snapshot
      _handleSupportTag()
    })
  }

  async function _handleSupportTag() {
    if (!participants.value) {
      return
    }

    const isSupportInvolved = participants.value.some(
      (_participant) => _participant.user.role === 'support' && _participant.user.id !== SUPPORT_USER.id
    )

    await conversation.value?.set({
      custom: { [SUPPORT_INVOLVED_TAG]: isSupportInvolved ? 'true' : null },
    })

    /**
     * conv disappeared because incompatible isSupportInvolved and SUPPORT_INVOLVED_TAG filter
     * therefore we need to select a new one
     */
    if (
      (filters.value.custom[SUPPORT_INVOLVED_TAG] === NOT_EXISTS_FILTER && isSupportInvolved) ||
      (filters.value.custom[SUPPORT_INVOLVED_TAG] === EXISTS_FILTER && !isSupportInvolved)
    ) {
      await inbox.value.select(undefined)
    }
  }

  function _getThemeLinks() {
    const dvmRoute = curUser.is.dvmValuer
      ? 'valuation.dvm'
      : curUser.is.onlyBorrower
        ? 'valuation.borrower'
        : 'valuation.request'

    const ovmRoute = curUser.is.ovmValuer
      ? 'valuation.valuer-app'
      : curUser.is.onlyBorrower
        ? 'valuation.borrower'
        : 'valuation.request'

    const oepcRoute = curUser.is.oepcValuer ? 'expert.summary' : 'oepc'

    const avmLink = router.resolve({ name: 'valuation.request', query: { valuation_request_ref: '' } })
    const dvmLink = router.resolve({ name: dvmRoute, query: { valuation_request_ref: '' } })
    const ovmLink = router.resolve({ name: ovmRoute, query: { valuation_request_ref: '' } })
    const ersLink = router.resolve({ name: 'ers', query: { valuation_request_ref: '' } })
    const oepcLink = router.resolve({ name: oepcRoute, query: { valuation_request_ref: '' } })

    return {
      avm: new URL(avmLink.href, window.location.origin).href,
      dvm: new URL(dvmLink.href, window.location.origin).href,
      ovm: new URL(ovmLink.href, window.location.origin).href,
      ers: new URL(ersLink.href, window.location.origin).href,
      oepc: new URL(oepcLink.href, window.location.origin).href,
    }
  }

  function _getTranslations() {
    return {
      borrower: t('monolith.inbox.borrower'),
      valuer: t('monolith.inbox.valuer'),
      ...ORGANIZATIONS,
    }
  }

  return reactive({
    INBOX_ID,
    SUPPORT_INBOX_ID,
    CHATBOX_ID,
    TAGS,
    SUPPORT_USER,
    startSession,
    createTalkPopup,
    openPopup,
    createInbox,
    destroyTalkPopup,
    setFilters,
    updateTags,
    persistTags,
    addUserToConversation,
    removeUserFromConversation,
    getConversations,
    selectConversationOverview,
    showGeneralInbox,
    totalUnreads,
    showTags,
    showAddUser,
    showRemoveUser,
    tags,
    enabled,
    conversation,
    participants,
    inbox,
    getCurrentSession,
  })
}

export default useTalkjs
